<!--@format-->
<template>
    <div class="list-upload-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget()">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="!loadingCustomFieldHeaders" class="widget-container">
                    <list-upload-widget-strategy-step
                        v-if="currentStep === steps.strategy"
                        ref="strategyStep"
                        @closeWidget="closeWidget()"
                        @nextStep="goToStep('listUpload')"
                    />
                    <list-upload-widget-upload-step
                        v-if="currentStep === steps.listUpload"
                        ref="uploadStep"
                        @nextStep="goToStep('matching')"
                        :customFieldHeaders="customFieldHeaders"
                    />
                    <list-upload-widget-matching-step
                        v-if="currentStep === steps.matching"
                        ref="matchingStep"
                        @list:saving="setLoadingStatus($event)"
                        @closeWidget="closeWidget()"
                        :customFieldHeaders="customFieldHeaders"
                    />
                </div>
                <div v-else class="loading-container">
                    <loading-screen-svg />
                </div>
            </div>
            <div v-if="!loadingCustomFieldHeaders" class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget()"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button v-if="currentStep === steps.strategy" @click="createList()">
                        Finish
                    </app-button>
                    <app-button
                        :loading="currentStep.loading"
                        class="save-btn"
                        @click="currentStep.action"
                    >
                        {{ currentStep.buttonText }} <m6-next-svg />
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingScreenSvg from '@/components/svg/loading-screen-svg.vue';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const ListUploadWidgetStrategyStep = () =>
    import('@/components/sliding-widgets/list-widget/list-upload-widget-strategy-step');
const ListUploadWidgetUploadStep = () =>
    import('@/components/sliding-widgets/list-widget/list-upload-widget-upload-step');
const ListUploadWidgetMatchingStep = () =>
    import('@/components/sliding-widgets/list-widget/list-upload-widget-matching-step');
const M6NextSvg = () => import('@/components/svg/grid/m6-next-svg');

import EventBus from '@/scripts/event-bus.js';
import { CustomFieldService } from '@/scripts/services/custom-fields/custom-field-service';

export default {
    name: 'list-upload-widget',

    components: {
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'list-upload-widget-strategy-step': ListUploadWidgetStrategyStep,
        'list-upload-widget-upload-step': ListUploadWidgetUploadStep,
        'list-upload-widget-matching-step': ListUploadWidgetMatchingStep,
        'm6-next-svg': M6NextSvg,
    },

    provide() {
        return {
            list: this.list,
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            currentStep: null,
            list: {
                id: null,
                clientId: this.widget.clientId,
                name: null,
                isMarketable: false,
                base64Encoding: null,
                columns: [],
                rows: [],
                fileExtension: null,
                isValid: true,
            },

            steps: {
                strategy: {
                    title: 'Upload a List',
                    buttonText: 'Next Step',
                    loading: false,
                    action: () => {
                        this.$refs.strategyStep.next();
                    },
                },

                listUpload: {
                    title: 'Upload a List',
                    buttonText: 'Next',
                    loading: false,
                    action: () => {
                        this.$refs.listUploadStep.next();
                    },

                    goBack: () => {
                        this.goToStep('strategy');
                    },
                },

                matching: {
                    title: 'Field Matching',
                    buttonText: 'Save',
                    loading: false,
                    action: () => {
                        this.$refs.matchingStep.next();
                    },
                },
            },
            customFieldHeaders: [],
            loadingCustomFieldHeaders: false,
        };
    },

    async created() {
        EventBus.$on('custom-field-added', this.getCustomFieldHeaders);

        this.getCustomFieldHeaders();

        if (this.widget.listId) {
            this.list.id = this.widget.listId;
        }

        this.goToStep('strategy');
    },

    beforeDestroy() {
        EventBus.$off('custom-field-added', this.getCustomFieldHeaders);
    },

    methods: {
        setLoadingStatus(loading) {
            this.currentStep.loading = loading;
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateLists() {
            EventBus.$emit('list:update');
        },

        createList() {
            this.$refs.strategyStep.listIsValid = !!this.list.name;

            if (this.$refs.strategyStep.listIsValid) {
                this.loading = true;

                var request = {
                    clientId: this.widget.clientId,
                    name: this.list.name,
                    isMarketable: this.list.isMarketable,
                };

                this.$api
                    .post('/api/Lists', request)
                    .then((response) => {
                        this.$router.push(`/Lists/${response.data.Id}`);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'There was an error creating your list',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },

        async getCustomFieldHeaders() {
            this.loadingCustomFieldHeaders = true;
            this.customFieldHeaders = [];

            const types = ['Contact', 'Company'];

            try {
                await Promise.all(
                    types.map((type) => {
                        return CustomFieldService.getCustomFieldHeaders(
                            type,
                            this.widget.clientId,
                        ).then((res) => {
                            const newHeaders = res.data.data;

                            newHeaders.forEach((header) => {
                                var customFieldType = this.mapHeaderType(header.InputType);

                                if (
                                    customFieldType !== 'check' &&
                                    customFieldType !== 'multicheck'
                                ) {
                                    var customHeader = {
                                        name: header.Name,
                                        value: `customField-${type}-${customFieldType}-${header.Id}`,
                                    };
                                    this.customFieldHeaders.push(customHeader);
                                }
                            });
                        });
                    }),
                );
            } catch (error) {
                this.$helpers.showFailNotification(
                    'An error occurred while loading your custom field headers.',
                    { error },
                );
            } finally {
                this.loadingCustomFieldHeaders = false;
            }
        },

        mapHeaderType(type) {
            switch (type) {
                case 'single-line text':
                    return 'single';
                case 'multi-line text':
                    return 'multi';
                case 'date picker':
                    return 'date';
                case 'single checkbox':
                    return 'check';
                case 'multiple checkboxes':
                    return 'multicheck';
                case 'number':
                    return 'number';
                case 'url':
                    return 'url';
            }
        },
    },
};
</script>

<style>
.list-upload-widget .widget-container {
    height: 100%;
}

.list-upload-widget .action-container {
    display: flex;
}

.list-upload-widget .action-container .save-btn {
    margin-left: 15px;
}
.list-upload-widget .action-container .save-btn .m6-next-svg {
    height: 12px;
    margin-left: 12px;
}
.list-upload-widget .action-container .save-btn .m6-next-svg path {
    margin-left: 15px;
    stroke: #fff;
    fill: #fff;
    stroke-width: 0;
    stroke-linecap: round;
}

.list-upload-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
    border: 1px solid rgba(202, 198, 203, 1);
    padding: 0px 30px;
}

.list-upload-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.list-upload-widget .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
}

.list-upload-widget .loading-screen {
    margin-left: 0 !important;
}
</style>
